// BotConfigPage — Configure AI Bot Trader
// Adjust risk, pairs, schedule, take profit/stop loss
// Layout: per-bot config + global settings

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlatformLogo } from '../../components/Brand';
import { TfCardList, TfSectionHeaderView } from '../../components/TfPrimitives';

export function BotConfigPage() {
  const nav = useNavigate();
  const [enabled, setEnabled] = useState(true);
  const [strategy, setStrategy] = useState('breakout');
  const [risk, setRisk] = useState(1.0);
  const [maxDD, setMaxDD] = useState(5);
  const [maxTrades, setMaxTrades] = useState(5);
  const [tp, setTp] = useState(80);
  const [sl, setSl] = useState(40);
  const [pairs, setPairs] = useState<string[]>(['ES', 'NQ', 'CL']);
  const [schedule, setSchedule] = useState('rth');
  const [saved, setSaved] = useState(false);

  const ALL_PAIRS = ['ES', 'NQ', 'YM', 'RTY', 'CL', 'GC', 'SI', 'BTC', 'ETH', 'EUR/USD', 'GBP/USD'];

  function togglePair(p: string) {
    setPairs((curr) => curr.includes(p) ? curr.filter((x) => x !== p) : [...curr, p]);
  }

  function save() {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-tf-bg/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-tf-text-1">Bot config</div>
        <button onClick={save} data-testid="save-bot" className="px-3 text-[15px] font-semibold text-tf-blue">
          {saved ? '✓ Saved' : 'Save'}
        </button>
      </div>

      {/* Hero — running state */}
      <div className="mx-4 mt-4">
        <div
          className="rounded-[20px] text-center p-5 text-white"
          style={{ background: enabled ? 'linear-gradient(135deg, #34C759 0%, #1E7E34 100%)' : '#1C1C1E' }}
        >
          <div className="text-[11px] uppercase tracking-wider text-white/70">Bot status</div>
          <div className="mt-1 text-[32px] font-bold tracking-tight">{enabled ? 'Running' : 'Stopped'}</div>
          <div className="mt-1 text-[13px] text-white/85">
            {enabled ? 'Breakout strategy · Started 2h 14m ago' : 'Last run: yesterday'}
          </div>
          <button
            onClick={() => setEnabled(!enabled)}
            data-testid="bot-toggle"
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur-md px-4 py-2 active:opacity-40"
          >
            <span className="h-2 w-2 rounded-full bg-white" />
            <span className="text-[13px] font-semibold">{enabled ? 'Stop bot' : 'Start bot'}</span>
          </button>
        </div>
      </div>

      {/* Strategy */}
      <TfSectionHeaderView title="Strategy" />
      <div className="px-4 grid grid-cols-2 gap-2">
        {[
          { key: 'breakout',     name: 'Breakout',     icon: '🚀', desc: 'Momentum breakouts' },
          { key: 'mean-revert',  name: 'Mean Rev.',    icon: '🔁', desc: 'Counter-trend fade' },
          { key: 'trend',        name: 'Trend Follow', icon: '📈', desc: 'Ride strong moves', locked: true },
          { key: 'sniper',       name: 'Sniper',       icon: '🎯', desc: 'Quick scalps',     locked: true },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => !s.locked && setStrategy(s.key)}
            disabled={s.locked}
            data-testid={`strategy-${s.key}`}
            className={['rounded-[14px] p-3 text-left transition',
              strategy === s.key ? 'bg-tf-text-1 text-white' : 'bg-tf-bg border border-tf-border text-tf-text-1',
              s.locked ? 'opacity-50' : 'active:scale-95'
            ].join(' ')}
          >
            <div className="flex items-center justify-between">
              <span className="text-[18px]">{s.icon}</span>
              {s.locked && <span className="text-[9px] font-bold uppercase text-tf-warning">🔒 Pro</span>}
            </div>
            <div className="mt-1.5 text-[14px] font-bold">{s.name}</div>
            <div className={['text-[11px]', strategy === s.key ? 'text-white/70' : 'text-tf-text-3'].join(' ')}>{s.desc}</div>
          </button>
        ))}
      </div>

      {/* Risk settings */}
      <TfSectionHeaderView title="Risk management" />
      <div className="mx-4">
        <TfCardList>
          <SliderRow label="Risk per trade" value={`${risk.toFixed(1)}%`} sub="of total equity" valueLabel={`1.0% of $50,000 = $500`} testid="risk-trade">
            <input type="range" min="0.25" max="5" step="0.25" value={risk} onChange={(e) => setRisk(Number(e.target.value))} className="w-full accent-tf-blue" data-testid="slider-risk" />
          </SliderRow>
          <SliderRow label="Max daily loss" value={`${maxDD}%`} sub="auto-stop if hit" valueLabel={`$${(50000 * maxDD / 100).toFixed(0)}`} testid="risk-maxdd">
            <input type="range" min="1" max="20" step="1" value={maxDD} onChange={(e) => setMaxDD(Number(e.target.value))} className="w-full accent-tf-loss" data-testid="slider-maxdd" />
          </SliderRow>
          <SliderRow label="Max concurrent trades" value={`${maxTrades}`} sub="open positions" valueLabel={maxTrades === 1 ? '1 trade' : `${maxTrades} trades`} testid="risk-trades">
            <input type="range" min="1" max="10" step="1" value={maxTrades} onChange={(e) => setMaxTrades(Number(e.target.value))} className="w-full accent-tf-blue" data-testid="slider-trades" />
          </SliderRow>
          <SliderRow label="Take profit" value={`${tp} ticks`} sub="per trade" valueLabel="Auto-close" testid="risk-tp">
            <input type="range" min="10" max="200" step="10" value={tp} onChange={(e) => setTp(Number(e.target.value))} className="w-full accent-tf-profit" data-testid="slider-tp" />
          </SliderRow>
          <SliderRow label="Stop loss" value={`${sl} ticks`} sub="per trade" valueLabel="Auto-close" last testid="risk-sl">
            <input type="range" min="10" max="200" step="10" value={sl} onChange={(e) => setSl(Number(e.target.value))} className="w-full accent-tf-loss" data-testid="slider-sl" />
          </SliderRow>
        </TfCardList>
      </div>

      {/* Pairs */}
      <TfSectionHeaderView title="Pairs to trade" viewAllLabel="Filter" onViewAll={() => alert('Filter pairs')} />
      <div className="mx-4 flex flex-wrap gap-1.5">
        {ALL_PAIRS.map((p) => (
          <button
            key={p}
            onClick={() => togglePair(p)}
            data-testid={`pair-${p}`}
            className={['rounded-full px-3 py-1.5 text-[13px] font-semibold transition',
              pairs.includes(p) ? 'bg-tf-blue text-white' : 'bg-tf-bg border border-tf-border text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Schedule */}
      <TfSectionHeaderView title="Schedule" />
      <div className="mx-4">
        <TfCardList>
          {[
            { key: 'rth',     label: 'Regular trading hours', sub: '9:30 AM - 4:00 PM ET' },
            { key: 'eth',     label: 'Extended hours',         sub: '4:00 PM - 8:00 PM ET' },
            { key: '24h',     label: '24/7',                   sub: 'Crypto only' },
            { key: 'custom',  label: 'Custom',                 sub: 'Set your own times' },
          ].map((s, i, a) => (
            <button
              key={s.key}
              onClick={() => setSchedule(s.key)}
              data-testid={`schedule-${s.key}`}
              className={['flex w-full items-center gap-3 px-4 py-3 active:bg-tf-surface-1 transition', i < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-purple/15 text-[18px]">⏰</div>
              <div className="flex-1 text-left">
                <div className="text-[14px] font-semibold text-tf-text-1">{s.label}</div>
                <div className="text-[11px] text-tf-text-3">{s.sub}</div>
              </div>
              {schedule === s.key && (
                <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-blue" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>
              )}
            </button>
          ))}
        </TfCardList>
      </div>

      {/* Platform */}
      <TfSectionHeaderView title="Connected platform" />
      <div className="mx-4">
        <TfCardList>
          <div className="flex items-center gap-3 px-4 py-3">
            <PlatformLogo name="deepchart" size={32} />
            <div className="flex-1">
              <div className="text-[14px] font-semibold text-tf-text-1">Deepchart®</div>
              <div className="text-[11px] text-tf-profit">● Active · 12 sec ago</div>
            </div>
            <button className="text-tf-blue text-[12px] font-semibold">Change</button>
          </div>
        </TfCardList>
      </div>

      {/* Save CTA */}
      <div className="mx-4 mt-4">
        <button
          onClick={save}
          data-testid="save-bot-bottom"
          className="h-12 w-full rounded-full bg-tf-text-1 text-[16px] font-semibold text-white active:scale-[0.99]"
        >
          {saved ? '✓ Saved' : 'Save configuration'}
        </button>
      </div>
    </div>
  );
}

function SliderRow({ label, value, sub, valueLabel, children, last, testid }: { label: string; value: string; sub: string; valueLabel?: string; children: React.ReactNode; last?: boolean; testid?: string }) {
  return (
    <div data-testid={testid} className={['px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[14px] font-medium text-tf-text-1">{label}</div>
          <div className="text-[11px] text-tf-text-3">{sub}</div>
        </div>
        <div className="text-right">
          <div className="text-[15px] font-bold tabular-nums text-tf-blue">{value}</div>
          {valueLabel && <div className="text-[10px] text-tf-text-3">{valueLabel}</div>}
        </div>
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
